import { Stack, Text } from '@mantine/core';
import { useMemo } from 'react';
import { FieldValues } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import {
  AbsenceMotivation,
  TravelActivityType,
  absenceMotivationLabel,
  absenceMotivationList,
  mobilitySupportExcludedMotivations,
} from '@/module/Travel/Request/model/TravelRequestTypes';

import { EnumSelectControl, EnumSelectControlProps } from '@/core/components';

export type AbsenceMotivationRefusal =
  | 'withPaperNeedsPaper'
  | 'withPaperNeedsScientificEvent'
  | 'professionalDevelopmentNotForScientificEvent'
  | 'noCapDaysLeft'
  | 'other';

export interface AbsenceMotivationSelectControlProps<T extends FieldValues>
  extends Omit<EnumSelectControlProps<T>, 'options'> {
  allowedMotivations?: AbsenceMotivation[];
  activityType?: TravelActivityType;
  // The yearly cap can pay none of this trip's working days.
  noCapDaysLeft?: boolean;
  // A stage with UMFST mobility support is never offered the motivations paid from the yearly cap.
  withMobilitySupport?: boolean;
}

const capMotivations: AbsenceMotivation[] = ['WITH_PAPER_WITHIN_CAP', 'PROFESSIONAL_DEVELOPMENT_WITHIN_5'];

function activityRefusal(motivation: AbsenceMotivation, activityType?: TravelActivityType): AbsenceMotivationRefusal {
  const isScientificEvent = activityType === 'SCIENTIFIC_EVENT';
  if (motivation === 'WITH_PAPER_WITHIN_CAP') {
    // A scientific event refuses the with-paper motivation only while no paper is presented.
    return isScientificEvent ? 'withPaperNeedsPaper' : 'withPaperNeedsScientificEvent';
  }
  if (motivation === 'PROFESSIONAL_DEVELOPMENT_WITHIN_5' && isScientificEvent) {
    return 'professionalDevelopmentNotForScientificEvent';
  }
  return 'other';
}

export function absenceMotivationRefusals(
  allowedMotivations: AbsenceMotivation[] | undefined,
  activityType?: TravelActivityType,
  noCapDaysLeft = false,
): Partial<Record<AbsenceMotivation, AbsenceMotivationRefusal>> {
  const refusals: Partial<Record<AbsenceMotivation, AbsenceMotivationRefusal>> = {};
  if (allowedMotivations == null) {
    return refusals;
  }
  absenceMotivationList.forEach(motivation => {
    if (!allowedMotivations.includes(motivation)) {
      refusals[motivation] = activityRefusal(motivation, activityType);
    } else if (noCapDaysLeft && capMotivations.includes(motivation)) {
      refusals[motivation] = 'noCapDaysLeft';
    }
  });
  return refusals;
}

export function AbsenceMotivationSelectControl<T extends FieldValues>(props: AbsenceMotivationSelectControlProps<T>) {
  const { allowedMotivations, activityType, noCapDaysLeft, withMobilitySupport = false, ...rest } = props;
  const { t } = useTranslation();
  const offered = useMemo(
    () =>
      absenceMotivationList.filter(
        value => !withMobilitySupport || !mobilitySupportExcludedMotivations.includes(value),
      ),
    [withMobilitySupport],
  );
  const refusals = useMemo(() => {
    const all = absenceMotivationRefusals(allowedMotivations, activityType, noCapDaysLeft);
    return Object.fromEntries(
      Object.entries(all).filter(([motivation]) => offered.includes(motivation as AbsenceMotivation)),
    ) as Partial<Record<AbsenceMotivation, AbsenceMotivationRefusal>>;
  }, [allowedMotivations, activityType, noCapDaysLeft, offered]);
  const options = useMemo(
    () =>
      offered.map(value => ({
        value,
        label: absenceMotivationLabel(value, t),
        disabled: refusals[value] != null,
      })),
    [t, offered, refusals],
  );

  return (
    <Stack gap={4}>
      <EnumSelectControl
        {...rest}
        options={options}
      />
      {[...new Set(Object.values(refusals))].map(refusal => (
        <Text
          key={refusal}
          size="xs"
          c="dimmed"
        >
          {t(`travel.requests.form.motivationUnavailable.${refusal}`, {
            beyondCap: absenceMotivationLabel('BEYOND_CAP_ON_LEAVE', t),
          })}
        </Text>
      ))}
    </Stack>
  );
}
